/**
 * RunRetrospectiveDialog — NC-1281 S3.
 *
 * The Run-Rueckblick: one observation about one finished run, by whoever ran it.
 * Only offered once the run is over (`expired` / `stopped`) — asking "how did it
 * go?" while it is still running would collect a guess.
 *
 * The load-bearing detail is that the endpoint **upserts** on `retro-<runId>`:
 * a second POST replaces the first document wholesale. A form that opened blank
 * would therefore silently wipe the other three fields as soon as someone
 * revisited it to change one of them. So the dialog LOADS the existing entry
 * first (via the workload profile, which is the only read path for this
 * partition) and pre-fills it.
 *
 * When that read is unavailable the dialog says so instead of pretending the
 * form is empty because nothing was written — the API keeps "container
 * unreadable" (`available: false`) apart from "nothing yet" on purpose, and
 * collapsing the two here would re-introduce exactly the overwrite it warns
 * about.
 */

import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { RateReview as ReviewIcon } from '@mui/icons-material';
import { runsApi } from '../services/api';
import { workloadProfileApi } from '../services/workloadProfileService';
import { RunRetrospective, RunRetroVerdict, RunStatus } from '../services/types';
import { formatDateSafe } from '../utils/dateFormat';
import {
  RUN_RETRO_VERDICTS,
  runRetroColor,
  runRetroLabel,
} from '../utils/retrospectiveLabels';

interface RunRetrospectiveDialogProps {
  runId: string;
  /** Needed to read the existing entry — the partition is keyed by workload. */
  workloadId?: string;
  runStatus?: RunStatus;
  /** operator+ — viewers see the state read-only. */
  canOperate: boolean;
}

/** A retrospective is a look BACK; offering it mid-run would collect a guess. */
const FINISHED_STATUSES: RunStatus[] = ['expired', 'stopped'];

export function isRetrospectiveOffered(status?: RunStatus): boolean {
  return !!status && FINISHED_STATUSES.includes(status);
}

const EMPTY_FORM = {
  verdict: 'as-planned' as RunRetroVerdict,
  whatWentWrong: '',
  participantFeedback: '',
  changeRequested: '',
};

export function RunRetrospectiveDialog({
  runId,
  workloadId,
  runStatus,
  canOperate,
}: RunRetrospectiveDialogProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [existing, setExisting] = useState<RunRetrospective | null>(null);
  /** true when we could not determine whether an entry already exists. */
  const [unknownExisting, setUnknownExisting] = useState(false);
  const [saved, setSaved] = useState<RunRetrospective | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);

  if (!isRetrospectiveOffered(runStatus)) return null;

  const handleOpen = async () => {
    setOpen(true);
    setError(null);
    setSaved(null);
    setUnknownExisting(false);
    setExisting(null);
    setForm(EMPTY_FORM);

    if (!workloadId) {
      // No workload reference → the POST would 400 anyway. Say why up front
      // rather than letting the save fail after the text is typed.
      setUnknownExisting(true);
      return;
    }

    setLoading(true);
    try {
      const result = await workloadProfileApi.get(workloadId);
      const retros = result.retrospectives;
      if (!retros || retros.available === false) {
        setUnknownExisting(true);
      } else {
        const found = retros.runRetros.find((r) => r.runId === runId) ?? null;
        setExisting(found);
        if (found) {
          setForm({
            verdict: found.verdict,
            whatWentWrong: found.whatWentWrong ?? '',
            participantFeedback: found.participantFeedback ?? '',
            changeRequested: found.changeRequested ?? '',
          });
        }
      }
    } catch {
      setUnknownExisting(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!canOperate || saving) return;
    setSaving(true);
    setError(null);
    try {
      const result = await runsApi.saveRetrospective(runId, {
        verdict: form.verdict,
        whatWentWrong: form.whatWentWrong,
        participantFeedback: form.participantFeedback,
        // '' and null both mean "nothing requested"; the server normalises, and
        // sending null keeps the two representations from multiplying here too.
        changeRequested: form.changeRequested.trim() === '' ? null : form.changeRequested,
      });
      setSaved(result);
      setExisting(result);
      setOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Rückblick konnte nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  };

  const current = saved ?? existing;

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Rückblick auf diesen Lauf
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Eine Beobachtung zu diesem konkreten Lauf — was lief, was nicht, was die Teilnehmer
          gesagt haben. Ein Rückblick je Lauf; ein zweiter ersetzt den ersten.
        </Typography>

        {current && (
          <Box sx={{ mb: 2 }} data-testid="retro-existing">
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Chip
                size="small"
                color={runRetroColor(current.verdict)}
                label={runRetroLabel(current.verdict)}
              />
              <Typography variant="caption" color="text.secondary">
                {current.createdBy} · {formatDateSafe(current.createdAt)}
              </Typography>
            </Stack>
            {current.whatWentWrong && (
              <Typography variant="body2">{current.whatWentWrong}</Typography>
            )}
          </Box>
        )}

        {error && !open && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Button
          variant="outlined"
          startIcon={<ReviewIcon />}
          disabled={!canOperate}
          onClick={() => void handleOpen()}
        >
          {current ? 'Rückblick bearbeiten' : 'Rückblick erfassen'}
        </Button>

        <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>Rückblick — Lauf {runId}</DialogTitle>
          <DialogContent>
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                <CircularProgress size={18} />
                <Typography variant="body2">Vorhandenen Rückblick laden…</Typography>
              </Box>
            ) : (
              <Stack spacing={2} sx={{ mt: 1 }}>
                {!workloadId && (
                  <Alert severity="warning" data-testid="retro-no-workload">
                    Dieser Lauf verweist auf keinen Workload. Ein Rückblick wird gegen den
                    Workload abgelegt und kann hier deshalb nicht gespeichert werden.
                  </Alert>
                )}

                {workloadId && unknownExisting && (
                  <Alert severity="warning" data-testid="retro-unknown-existing">
                    Ein bereits vorhandener Rückblick konnte nicht geladen werden. Speichern
                    <strong> ersetzt </strong>
                    einen etwaigen bestehenden Eintrag vollständig.
                  </Alert>
                )}

                {existing && (
                  <Alert severity="info" data-testid="retro-replaces">
                    Es gibt schon einen Rückblick von {existing.createdBy} (
                    {formatDateSafe(existing.createdAt)}). Speichern ersetzt ihn.
                  </Alert>
                )}

                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Ergebnis"
                  value={form.verdict}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, verdict: e.target.value as RunRetroVerdict }))
                  }
                >
                  {RUN_RETRO_VERDICTS.map((verdict) => (
                    <MenuItem key={verdict} value={verdict}>
                      {runRetroLabel(verdict)}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  size="small"
                  label="Was lief nicht"
                  value={form.whatWentWrong}
                  onChange={(e) => setForm((f) => ({ ...f, whatWentWrong: e.target.value }))}
                />

                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  size="small"
                  label="Rückmeldung der Teilnehmer"
                  value={form.participantFeedback}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, participantFeedback: e.target.value }))
                  }
                />

                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  size="small"
                  label="Änderungswunsch (optional)"
                  value={form.changeRequested}
                  onChange={(e) => setForm((f) => ({ ...f, changeRequested: e.target.value }))}
                />

                {error && (
                  <Alert severity="error" onClose={() => setError(null)}>
                    {error}
                  </Alert>
                )}
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>Abbrechen</Button>
            <Button
              variant="contained"
              disabled={!canOperate || saving || loading || !workloadId}
              onClick={() => void handleSave()}
            >
              {saving ? 'Speichert…' : 'Speichern'}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
}

export default RunRetrospectiveDialog;
